import { useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import { useTranslation } from 'react-i18next';
import { IoArrowBackSharp } from 'react-icons/io5';

import { NuveiPayment } from '../NuveiPayment/NuveiPayment';
import { useCheckout } from '../../../../hooks/use-checkout';
import { usePaymentState } from '../../hooks/use-payment-state';
import { FormFooter } from './components/FormFooter/FormFooter';
import { Paymentmethods } from '../PaymentMethods/PaymentMethods';
import { AddressElement } from '../AddressElement/AddressElement';
import { mobileSDKUtils } from '../../../../utils/mobile-sdk-utils';
import { PaypalApmButton } from '../PaypalApmButton/PaypalApmButton';
import { useCheckoutFlowType } from '../../hooks/use-checkout-flow-type';
import { EBEPaymentMethodsName, ECustomEvents, EPaymentProvider } from '../../../../contracts';
import { AydenPaymentElement } from '../AdyenPaymentElement/AdyenPaymentElement';
import { StripePaymentWrapper } from '../StripePaymentElement/StripePaymentWrapper';
import { PaymentElementSkeleton } from '../../../../Skeltons/PaymentElementSkeleton';
import { BraintreePayment } from '../BraintreePaymentElement/BraintreePaymentElement';
import { useShowTaxCalculationFields } from '../../hooks/use-show-tax-calculation-fields';

import './style.scss';
import { sendProductEvent } from '../../../../utils/mixpanel-utils';

interface PaymentFormProps {
  setOverlay: (value: boolean) => void;
  setApmTab: (value: any) => void;
  showInvalidZipMode: boolean;
  onZipCodeModeBackClicked: () => void;
}

export function PaymentForm({
  setOverlay,
  setApmTab,
  showInvalidZipMode,
  onZipCodeModeBackClicked,
}: PaymentFormProps) {
  const [isHiddenApmsShown, setIsHiddenApmsShown] = useState<boolean>(false);

  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const isInitialRender = useRef<boolean>(false);
  const { isCheckoutDataLoading, selectedPaymentMethod, getEventParams } = useCheckout();
  const { showAddressField } = useShowTaxCalculationFields();
  const errorMessage = usePaymentState((state) => state.errorMessage);
  const isPaymentElementLoading = usePaymentState((state) => state.isPaymentElementLoading);
  const isExpressCheckoutLoading = usePaymentState((state) => state.isExpressCheckoutLoading);
  const setIsApmLoading = usePaymentState((state) => state.setIsApmLoading);
  const isApmLoading = usePaymentState((state) => state.isApmLoading);
  const { isPaymentLinksFlow } = useCheckoutFlowType();

  const isPaymentSideLoading = isCheckoutDataLoading || isPaymentElementLoading;
  const isNuvei = selectedPaymentMethod?.provider === EPaymentProvider.nuvei;
  const isStripe = selectedPaymentMethod?.provider === EPaymentProvider.stripe;
  const isBraintree = selectedPaymentMethod?.provider === EPaymentProvider.braintree;
  const isAdyen = selectedPaymentMethod?.provider === EPaymentProvider.adyen;
  const isPaypal = selectedPaymentMethod?.paymentMethodName === EBEPaymentMethodsName.paypal;

  const hidePaymentElement = isHiddenApmsShown || showInvalidZipMode;
  const hidePaymentMethods =
    showInvalidZipMode || mobileSDKUtils.isMobileSdk() || isPaymentLinksFlow;
  const showSkeletons = isPaymentLinksFlow
    ? isExpressCheckoutLoading || isApmLoading
    : isPaymentSideLoading || isApmLoading;

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    sendProductEvent(ECustomEvents.CHECKOUT_PRESENTED, { ...getEventParams() });
  }, [isHiddenApmsShown]);

  return (
    <Box className="payment-form">
      <div className={`form-and-apms`}>
        {hidePaymentMethods ? null : !isPaymentSideLoading ? (
          <Paymentmethods
            setIsHiddenApmsShown={setIsHiddenApmsShown}
            isHiddenApmsShown={isHiddenApmsShown}
          />
        ) : // <Box className="visible-apms" mb={isMobile ? 1.5 : 3} mt={isMobile ? 1.5 : 3}>
        //   <Skeleton variant="rectangular" width={68} height={68} sx={{ marginRight: '8px' }} />
        //   <Skeleton variant="rounded" width={68} height={68} sx={{ marginRight: '8px' }} />
        //   <Skeleton variant="rounded" width={68} height={68} sx={{ marginRight: '8px' }} />
        //   <Skeleton variant="rounded" width={68} height={68} />
        // </Box>
        null}

        {(isPaymentSideLoading ||
          isApmLoading ||
          (isPaymentLinksFlow && isExpressCheckoutLoading)) && (
          <div className="payment-elements-skleton">
            <PaymentElementSkeleton />
            <PaymentElementSkeleton />
            <PaymentElementSkeleton />
            {(isPaymentSideLoading || (isPaymentLinksFlow && isExpressCheckoutLoading)) && (
              <Skeleton variant="rounded" width={'100%'} height={45} />
            )}
          </div>
        )}

        {showInvalidZipMode && (
          <Stack pt={2} spacing={1}>
            <Stack
              onClick={() => {
                setIsApmLoading(true);
                onZipCodeModeBackClicked();
              }}
              direction={'row'}
              alignItems={'center'}
              spacing={0.5}
              style={{ cursor: 'pointer' }}
            >
              <IoArrowBackSharp />
              <Box fontWeight={600}>{t('back')}</Box>
            </Stack>
            <Box className="invalid-zip-express-checkout">{t('provideValidZipCode')}</Box>
            <AddressElement zipOnly={!showAddressField} />
          </Stack>
        )}

        {!hidePaymentElement && (
          <div className={`form ${showSkeletons ? '' : 'show-payment-element'} `}>
            {isNuvei && <NuveiPayment />}
            {isStripe && (
              <StripePaymentWrapper
                key={selectedPaymentMethod?.paymentMethodName}
                setOverlay={setOverlay}
                setApmTab={setApmTab}
              />
            )}
            {isBraintree && <BraintreePayment />}
            {isAdyen && <AydenPaymentElement />}
            {isPaypal && <PaypalApmButton />}
          </div>
        )}
        {errorMessage && <div id="payment-message">{errorMessage}</div>}
      </div>
      {!isPaymentSideLoading && <FormFooter isHiddenApmsShown={isHiddenApmsShown} />}
    </Box>
  );
}
